import { transition } from '@helpers/animate';
import brand, { ThemeOption } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { default as SvgBack, default as SvgChevron } from '@img/icons/chevron-right.svg';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.nav `
  ${transition('transform, opacity')};
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background-color: ${brand.white};
  color: var(--fgColor);
  height: calc(100vh - 71px);
  opacity: 0;
  overflow-y: auto;
  padding: 0 0 30px;
  pointer-events: none;
  width: 100%;
  transform: translateY(100%) translateY(10px);
  z-index: 100;
  --backgroundTint: ${brand.white};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--backgroundTint);
    z-index: -1;
    pointer-events: none;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  &[data-sub-level='true'] {
    --backgroundTint: ${rgba(brand.orange, 0.1)};

    &[data-component-theme=${ThemeOption.Orange}] {
      --backgroundTint: ${rgba(brand.orange, 0.1)};
    }

    &[data-component-theme=${ThemeOption.Blue}] {
      --backgroundTint: ${rgba(brand.blue, 0.1)};
    }

    &[data-component-theme=${ThemeOption.Purple}] {
      --backgroundTint: ${rgba(brand.purple, 0.1)};
    }

    &[data-component-theme=${ThemeOption.Red}] {
      --backgroundTint: ${rgba(brand.red, 0.1)};
    }

    &[data-component-theme=${ThemeOption.Green}] {
      --backgroundTint: ${rgba(brand.green.default, 0.1)};
    }

    &[data-component-theme=${ThemeOption.DarkGreen}] {
      --backgroundTint: ${rgba(brand.green.dark, 0.1)};
    }

    &[data-component-theme=${ThemeOption.Teal}] {
      --backgroundTint: ${rgba(brand.teal, 0.1)};
    }
  }

  &[data-m-active='true'] {
    opacity: 1;
    pointer-events: all;
    transform: translateY(100%);
  }
`;
const TopArea = styled.div `
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  padding: ${fluid(12, 36)} 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ActionWrapper = styled.div `
  grid-column: 3 / span 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${fluid(18, 36)};

  @media ${from(Device.Tablet)} {
    grid-column: 4 / span 5;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 5 / span 8;
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};

  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  color: ${brand.black};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  > svg {
    display: block;
    height: 16px;
    width: 16px;
  }

  ${ActionWrapper}[data-action-open='false'] & {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const ListArea = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
  border-top: 6px solid ${brand.orange};
`;
const List = styled.ul `
  padding: 15px 0;
  flex: 0 0 auto;
`;
const Item = styled.li ``;
const Arrow = styled(SvgChevron) `
  margin-left: auto;
  margin-right: 0;
  height: 26px;
`;
const Link = styled.a `
  display: flex;
  align-items: center;
  color: ${brand.black};
  background-color: ${rgba(brand.white, 0.05)};
  font-weight: 900;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 14px 35px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  &[data-active-page='true'] {
    color: ${brand.blue};
    background-color: ${brand.white};

    &:after {
      background-color: ${brand.white};
    }
  }

  svg {
    margin-right: 10px;
  }

  ${Arrow} {
    margin-right: 0;
  }
`;
const ChildLink = styled.div `
  ${Link} {
    padding: 8px 35px;
  }
`;
const Text = styled.span `
  ${fonts.agBook.bold};
  font-size: 24px;
  line-height: 29px;

  ${ChildLink} & {
    font-size: 20px;
    line-height: 28px;
  }

  svg {
    margin-right: 5px;
  }

  svg,
  span {
    vertical-align: middle;
  }
`;
const BackSvg = styled(SvgBack) `
  margin-left: auto;
  height: 15px;
`;
const Header = styled.header `
  ${Link} {
    padding-top: 40px;
    padding-bottom: 10px;

    ${Text} {
      span {
        text-decoration: underline;
      }
    }
  }
`;
const Back = styled.button `
  display: flex;
  gap: 10px;
  align-items: center;
  background: ${brand.orange};
  border: 0;
  margin: 0;
  padding: 15px 35px;
  color: ${brand.white};
  width: 100%;

  span {
    ${fonts.agBook.bold};
    flex: 1 0 auto;
    font-size: 24px;
    line-height: 28px;
    position: relative;
    text-align: left;
  }

  > svg {
    height: 26px;
    transform: scaleX(-1);
  }

  ${Container}[data-component-theme=${ThemeOption.Orange}] & {
    background: ${brand.orange};
  }

  ${Container}[data-component-theme=${ThemeOption.Blue}] & {
    background: ${brand.blue};
  }

  ${Container}[data-component-theme=${ThemeOption.Purple}] & {
    background: ${brand.purple};
  }

  ${Container}[data-component-theme=${ThemeOption.Red}] & {
    background: ${brand.red};
  }

  ${Container}[data-component-theme=${ThemeOption.Green}] & {
    background: ${brand.green.default};
  }

  ${Container}[data-component-theme=${ThemeOption.DarkGreen}] & {
    background: ${brand.green.dark};
  }

  ${Container}[data-component-theme=${ThemeOption.Teal}] & {
    background: ${brand.teal};
  }
`;
const Current = styled(Link) `
  color: ${brand.white};
  text-decoration: none;
  margin-bottom: 10px;
`;
const SmallList = styled(List) `
  padding: 5px 0;
`;
const SmallItem = styled(Item) ``;
const SmallLink = styled(Link) `
  color: ${brand.black};
  font-weight: 400;
  font-size: 16px;
`;
const LogOutBtn = styled.button `
  display: flex;
  align-items: center;
  color: ${brand.black};
  border: none;
  background-color: ${rgba(brand.white, 0.05)};
  font-weight: 400;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 14px 10px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;
  width: 100%;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  svg {
    margin-right: 10px;
  }

  &:focus,
  &:hover {
    color: ${brand.black};
    background-color: ${brand.white};
    text-decoration: none;
  }
`;
const HomeLink = styled.a ``;
const NavLogo = styled.div ``;
const LogoMedia = styled.img `
  display: block;
  max-height: ${fluid(40, 52)};
`;
const AccountBtnHolder = styled.div `
  grid-column: 12;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const AccountBtn = styled.a `
  background-color: transparent;
  display: block;
  border: 2px solid ${brand.white};
  border-radius: 9000000px;
  width: 40px;
  height: 40px;
  padding: 3px;
  text-decoration: none;
`;
const AccountBtnInner = styled.div `
  font-weight: 700;
  font-size: 14px;
  color: ${brand.black};
  border-radius: 9000000px;
  width: 30px;
  height: 30px;
  background-color: ${brand.white};
  display: flex;
  justify-content: center;
  align-items: center;
`;
const LinkArea = styled.div `
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ContactBtn = styled.div `
  width: 100%;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MemberLink = styled.a `
  ${fonts.agBook.regular};
  width: 100%;
  font-size: 20px;
  line-height: 24px;
  display: block;
  background-color: ${brand.orange};
  color: ${brand.white};
  text-decoration: none;
  padding: 20px 35px;
`;
const LoginLink = styled.a `
  ${fonts.agBook.regular};
  width: 100%;
  font-size: 18px;
  line-height: 22px;
  display: flex;
  gap: 15px;
  padding: 20px 35px;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  color: ${brand.black};
  border-bottom: 1px solid ${brand.black};

  svg {
    height: 20px;
  }
`;
const SearchForm = styled.div `
  padding: 20px 35px;
  display: inline-flex;
  align-items: center;

  background-color: ${brand.grey.grey91};
`;
const SearchLabel = styled.label `
  ${srOnly};
`;
const SearchInput = styled.input `
  ${fonts.agBook.light};
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 0;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid ${brand.black};
  flex: 1 0 auto;
  padding: 0 5px;
  height: 26px;
`;
const SearchSubmit = styled.button `
  background-color: transparent;
  color: ${brand.black};
  display: block;
  border: none;
  order: 1;
  padding: 10px 10px 10px 0;

  svg {
    height: 20px;
  }
`;
const MobileNavigationStyles = {
    AccountBtn,
    AccountBtnHolder,
    AccountBtnInner,
    Arrow,
    Back,
    BackSvg,
    ChildLink,
    CloseButton,
    ContactBtn,
    Container,
    Current,
    Header,
    HomeLink,
    Item,
    Link,
    LinkArea,
    List,
    ListArea,
    LoginLink,
    LogoMedia,
    LogOutBtn,
    MemberLink,
    NavLogo,
    SearchForm,
    SearchInput,
    SearchLabel,
    SearchSubmit,
    SmallItem,
    SmallLink,
    SmallList,
    Text,
    TopArea,
};
export default MobileNavigationStyles;
